import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationCenter = ({ 
  notifications = [],
  onMarkAsRead = () => {},
  onMarkAllAsRead = () => {},
  onResolveIssue = () => {},
  onDismiss = () => {}
}) => {
  const [filter, setFilter] = useState('all');

  const defaultNotifications = [
    {
      id: 'notif-1',
      type: 'dispute',
      priority: 'high',
      title: 'Disputa en Mesa 12',
      message: 'Múltiples participantes han seleccionado el mismo artículo "Jamón Ibérico"',
      sessionId: 'session-1',
      tableName: 'Mesa 12 - Cena Familiar',
      timestamp: '2025-11-14T20:25:00',
      isRead: false,
      actionRequired: true
    },
    {
      id: 'notif-2',
      type: 'timeout',
      priority: 'medium',
      title: 'Sesión Inactiva',
      message: 'Mesa 8 lleva más de 30 minutos sin actividad',
      sessionId: 'session-2',
      tableName: 'Mesa 8 - Reunión Trabajo',
      timestamp: '2025-11-14T20:20:00',
      isRead: false,
      actionRequired: true
    },
    {
      id: 'notif-3',
      type: 'payment',
      priority: 'medium',
      title: 'Pago Pendiente',
      message: 'Ana Martín no ha completado el pago en Mesa 5',
      sessionId: 'session-3',
      tableName: 'Mesa 5 - Cumpleaños',
      timestamp: '2025-11-14T20:15:00',
      isRead: false,
      actionRequired: false
    },
    {
      id: 'notif-4',
      type: 'completion',
      priority: 'low',
      title: 'Sesión Completada',
      message: 'Mesa 3 ha completado exitosamente el proceso de división',
      sessionId: 'session-4',
      tableName: 'Mesa 3 - Almuerzo',
      timestamp: '2025-11-14T20:10:00',
      isRead: true,
      actionRequired: false
    },
    {
      id: 'notif-5',
      type: 'error',
      priority: 'high',
      title: 'Error de Sistema',
      message: 'Fallo en la sincronización de datos para Mesa 15',
      sessionId: 'session-5',
      tableName: 'Mesa 15 - Cena Romántica',
      timestamp: '2025-11-14T20:05:00',
      isRead: false,
      actionRequired: true
    }
  ];

  const allNotifications = notifications?.length > 0 ? notifications : defaultNotifications;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-success';
      default:
        return 'text-text-secondary';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'dispute':
        return 'AlertTriangle';
      case 'timeout':
        return 'Clock';
      case 'payment':
        return 'CreditCard';
      case 'completion':
        return 'CheckCircle';
      case 'error':
        return 'XCircle';
      default:
        return 'Bell';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'dispute':
        return 'bg-error/10 text-error';
      case 'timeout':
        return 'bg-warning/10 text-warning';
      case 'payment':
        return 'bg-primary/10 text-primary';
      case 'completion':
        return 'bg-success/10 text-success';
      case 'error':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Ahora';
    if (diffMinutes < 60) return `Hace ${diffMinutes}m`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `Hace ${diffHours}h`;
    
    return time?.toLocaleDateString('es-ES');
  };

  const filteredNotifications = allNotifications?.filter(notification => {
    switch (filter) {
      case 'unread':
        return !notification?.isRead;
      case 'high':
        return notification?.priority === 'high';
      case 'action-required':
        return notification?.actionRequired;
      default:
        return true;
    }
  });

  const unreadCount = allNotifications?.filter(n => !n?.isRead)?.length;
  const highPriorityCount = allNotifications?.filter(n => n?.priority === 'high' && !n?.isRead)?.length;

  const filters = [
    { value: 'all', label: 'Todas', count: allNotifications?.length },
    { value: 'unread', label: 'No Leídas', count: unreadCount },
    { value: 'high', label: 'Alta Prioridad', count: highPriorityCount },
    { value: 'action-required', label: 'Acción Requerida', count: allNotifications?.filter(n => n?.actionRequired)?.length }
  ];

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Bell" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">
              Centro de Notificaciones
            </h3>
            {unreadCount > 0 && (
              <span className="bg-error text-error-foreground text-xs px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onMarkAllAsRead}
              iconName="CheckCheck"
              iconSize={14}
            >
              Marcar Todas
            </Button>
          )}
        </div>
      </div>
      {/* Filters */}
      <div className="p-4 border-b border-border">
        <div className="flex flex-wrap gap-2">
          {filters?.map((filterOption) => (
            <Button
              key={filterOption?.value}
              variant={filter === filterOption?.value ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(filterOption?.value)}
              className="text-sm"
            >
              {filterOption?.label}
              {filterOption?.count > 0 && (
                <span className="ml-1 text-xs opacity-75">({filterOption?.count})</span>
              )}
            </Button>
          ))}
        </div>
      </div>
      {/* Notifications List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredNotifications?.length > 0 ? (
          <div className="divide-y divide-border">
            {filteredNotifications?.map((notification) => (
              <div
                key={notification?.id}
                className={`p-4 hover:bg-muted/30 transition-colors ${
                  !notification?.isRead ? 'bg-primary/5 border-l-4 border-l-primary' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  {/* Icon */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getTypeColor(notification?.type)}`}>
                    <Icon name={getTypeIcon(notification?.type)} size={16} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className={`text-sm font-medium ${!notification?.isRead ? 'text-foreground' : 'text-text-secondary'}`}>
                            {notification?.title}
                          </h4>
                          <span className={`text-xs ${getPriorityColor(notification?.priority)}`}>
                            {notification?.priority === 'high' ? 'Alta' : 
                             notification?.priority === 'medium' ? 'Media' : 'Baja'}
                          </span>
                        </div>
                        
                        <p className="text-sm text-text-secondary mt-1">
                          {notification?.message}
                        </p>
                        
                        <div className="flex items-center space-x-2 mt-2">
                          <span className="text-xs text-text-secondary">
                            {notification?.tableName}
                          </span>
                          <span className="text-xs text-text-secondary">•</span>
                          <span className="text-xs text-text-secondary">
                            {formatTime(notification?.timestamp)}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-1 ml-2">
                        {notification?.actionRequired && (
                          <Button
                            variant="outline"
                            size="xs"
                            onClick={() => onResolveIssue(notification?.id, notification?.sessionId)}
                            iconName="ExternalLink"
                            iconSize={12}
                          >
                            Resolver
                          </Button>
                        )}
                        
                        {!notification?.isRead && (
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => onMarkAsRead(notification?.id)}
                            iconName="Check"
                            iconSize={12}
                          />
                        )}
                        
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => onDismiss(notification?.id)}
                          iconName="X"
                          iconSize={12}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Icon name="CheckCircle" size={32} className="mx-auto text-success mb-2" />
            <p className="text-text-secondary">
              {filter === 'all' ? 'No hay notificaciones' : 'No hay notificaciones que coincidan con el filtro'}
            </p>
          </div>
        )}
      </div>
      {/* Footer */}
      {filteredNotifications?.length > 0 && (
        <div className="p-3 border-t border-border bg-muted/30 text-center">
          <span className="text-xs text-text-secondary">
            Mostrando {filteredNotifications?.length} de {allNotifications?.length} notificaciones
          </span>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;